import React, { useCallback, useContext, useState } from "react";
import { IconButton, TextField, Tooltip } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useMonaco } from "@monaco-editor/react";
import { AutoFixHigh } from "@mui/icons-material";

import { Flexbox, Loader2, SvgButton } from "@profiler/ui";
import { MAIN_COLOR } from "../constants";
import { AppContext } from "../context/AppContext";
import { applyFhirPath } from "../commands/apply-fhirpath";

export const TopBar = () => {
  const [expression, setExpression] = useState("");
  const [path, setPath] = useState("");
  const monaco = useMonaco();

  const {
    editorRef,
    response: { setResponse },
    loader: { isLoading, toggle: toggleLoader },
    alert: { toggle: toggleAlert },
  } = useContext(AppContext);

  const handleFormat = useCallback(() => {
    const editorText = editorRef.current.getModel()?.getValue();

    if (editorText) {
      const model = editorRef.current.getModel();
      if (model) {
        editorRef.current.executeEdits(
          null,
          [
            {
              range: model.getFullModelRange(),
              text: JSON.stringify(JSON.parse(editorText), null, 2),
            },
          ],
          monaco ? [new monaco.Selection(1, 1, 1, 1)] : undefined
        );
      }
    }
  }, [editorRef]);

  const applyFhirPathHandler = useCallback(async () => {
    const editorText = editorRef.current.getModel()?.getValue() || "";

    toggleLoader();
    const result = await applyFhirPath(editorText, expression, path);
    toggleLoader();

    if (result.message) {
      toggleAlert({
        type: result.type,
        message: result.message,
      });
    }

    setResponse(result.outcome || "");
  }, [expression, setResponse, path]);

  const onChange = useCallback(
    (e: { target: { value: string; name: string } }) => {
      if (e.target.name === "expression") {
        setExpression(e.target.value);
      }
      if (e.target.name === "path") {
        setPath(e.target.value);
      }
    },
    [setExpression, setPath]
  );

  return (
    <Flexbox
      flexFlow="row nowrap"
      justifyContent="end"
      alignItems="center"
      paddingTop={1}
      paddingLeft={2}
      paddingRight={32}
      paddingBottom={1}
      borderBottom={1}
      borderColor="divider"
    >
      <TextField
        sx={{ flex: 1, marginRight: 3, input: { fontSize: 14 } }}
        placeholder={"FHIRPath expression..."}
        size="small"
        name="expression"
        value={expression}
        onChange={onChange}
      />
      <TextField
        sx={{ flex: 1, marginRight: 3, input: { fontSize: 14 } }}
        placeholder={"Path..."}
        size="small"
        name="path"
        value={path}
        onChange={onChange}
      />
      <Tooltip title="Format JSON">
        <SvgButton
          padding={0}
          ml={2}
          backgroundColor="transparent"
          onClick={handleFormat}
        >
          <AutoFixHigh />
        </SvgButton>
      </Tooltip>
      {isLoading ? (
        <Loader2 visibility={"visible"} color={MAIN_COLOR} />
      ) : (
        <Tooltip title="Call apply FHIR-path operation">
          <IconButton onClick={applyFhirPathHandler} sx={{ padding: 0 }}>
            <PlayArrowIcon fontSize="large" sx={{ color: MAIN_COLOR }} />
          </IconButton>
        </Tooltip>
      )}
    </Flexbox>
  );
};
